const QURAN_PROXY_BASE = process.env.REACT_APP_QURAN_PROXY_BASE_URL || "";
const QURAN_PUBLIC_API_BASE = "https://api.quran.com/api/v4";
const CHAPTERS_URL = `${QURAN_PUBLIC_API_BASE}/chapters?language=ar`;
const TRANSLATION_DEFAULT_IDS = {
  fr: [31, 136, 779],
  en: [20, 85, 149],
};
const RECITATION_IDS = [7, 1, 2];
const TAFSEER_DEFAULT_IDS = {
  ar: [16, 14, 93],
  en: [169, 168, 817],
};
const AUDIO_BASE_URL = "https://verses.quran.com";
const LANGUAGES_URL = `${QURAN_PUBLIC_API_BASE}/resources/languages?language=en`;
const chapterNameCache = new Map();
const chapterMetaCache = new Map();
const translationLanguagesCache = [];
const translationResourceCacheByLanguage = new Map();
const recitationResourceCacheByLanguage = new Map();
const tafsirResourceCacheByLanguage = new Map();
const tafsirLanguagesCache = [];
let chapterFetchPromise = null;
let translationLanguagesFetchPromise = null;
const translationResourcesFetchByLanguage = new Map();
const recitationResourcesFetchByLanguage = new Map();
const tafsirResourcesFetchByLanguage = new Map();
let tafsirLanguagesFetchPromise = null;

const FALLBACK_TRANSLATION_LANGUAGES = [
  { code: "fr", name: "French", nativeName: "Francais", direction: "ltr" },
  { code: "en", name: "English", nativeName: "English", direction: "ltr" },
  { code: "ar", name: "Arabic", nativeName: "العربية", direction: "rtl" },
];

export const QURAN_DEFAULTS = {
  page: 1,
  scriptEdition: "qcf_v2",
  translationEdition: "",
  tafsirEdition: "",
  reciterEdition: "",
  mushaf: 1,
};

const buildPageQuery = ({ mushaf = 1 }) => {
  const params = new URLSearchParams({
    words: "true",
    mushaf: String(mushaf),
    word_fields: "code_v2,text_qpc_hafs,char_type_name,line_number,page_number,position",
  });

  return params.toString();
};

const toVersesBase = (base) => {
  const clean = (base || "").trim().replace(/\/$/, "");
  if (!clean) {
    return "";
  }

  if (clean.endsWith("/api/v4")) {
    return clean;
  }

  if (clean.endsWith("/content")) {
    return `${clean}/api/v4`;
  }

  return `${clean}/content/api/v4`;
};

const buildCandidateUrls = ({ page, mushaf = 1 }) => {
  const query = buildPageQuery({ mushaf });
  const proxyBase = toVersesBase(QURAN_PROXY_BASE);

  const candidates = [];
  if (proxyBase) {
    candidates.push(`${proxyBase}/verses/by_page/${page}?${query}`);
  }

  candidates.push(`${QURAN_PUBLIC_API_BASE}/verses/by_page/${page}?${query}`);
  return candidates;
};

const getChapterId = (verseKey) => {
  const chapterPart = String(verseKey || "").split(":")[0];
  const chapterId = Number(chapterPart);
  return Number.isFinite(chapterId) ? chapterId : null;
};

const loadChapterNames = async () => {
  if (chapterNameCache.size > 0) {
    return chapterNameCache;
  }

  if (!chapterFetchPromise) {
    chapterFetchPromise = fetch(CHAPTERS_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load chapter names.");
        }

        return response.json();
      })
      .then((payload) => {
        const chapters = Array.isArray(payload?.chapters) ? payload.chapters : [];
        chapters.forEach((chapter) => {
          const id = Number(chapter?.id);
          if (!Number.isFinite(id)) {
            return;
          }

          const preferredName =
            chapter?.name_arabic ||
            chapter?.name_simple ||
            chapter?.name_complex ||
            chapter?.translated_name?.name;

          chapterNameCache.set(id, preferredName || `سورة ${id}`);
          chapterMetaCache.set(id, {
            id,
            name: preferredName || `سورة ${id}`,
            versesCount: Number(chapter?.verses_count) || null,
            pages: Array.isArray(chapter?.pages) ? chapter.pages.map((item) => Number(item)).filter(Number.isFinite) : [],
            bismillahPre: Boolean(chapter?.bismillah_pre),
          });
        });

        return chapterNameCache;
      })
      .catch(() => chapterNameCache);
  }

  return chapterFetchPromise;
};

export const getChapterInfo = async (chapterId) => {
  const id = Number(chapterId);
  if (!Number.isFinite(id) || id < 1) {
    throw new Error("Invalid chapter id.");
  }

  await loadChapterNames();
  const info = chapterMetaCache.get(id);
  if (!info) {
    throw new Error(`Chapter ${id} not found.`);
  }

  return info;
};

export const getChapterList = async () => {
  await loadChapterNames();

  return Array.from(chapterMetaCache.values())
    .sort((a, b) => a.id - b.id)
    .map((item) => ({
      id: item.id,
      name: item.name,
      pages: Array.isArray(item.pages) ? item.pages : [],
      versesCount: item.versesCount,
      bismillahPre: Boolean(item.bismillahPre),
    }));
};

const flattenWords = (verses) =>
  verses.flatMap((verse) =>
    (verse.words || []).map((word, index) => ({
      id: word.id ?? `${verse.verse_key}-${index}`,
      verseKey: verse.verse_key,
      position: word.position ?? index + 1,
      pageNumber: Number(word.page_number) || Number(verse.page_number) || null,
      lineNumber: Number(word.line_number) || null,
      charTypeName: word.char_type_name || "word",
      codeV2: word.code_v2 || "",
      textQpcHafs: word.text_qpc_hafs || "",
    }))
  );

export const getQuranPage = async ({ page = QURAN_DEFAULTS.page, mushaf = QURAN_DEFAULTS.mushaf } = {}) => {
  const safePage = Math.max(1, Math.min(604, Number(page) || 1));
  const urls = buildCandidateUrls({ page: safePage, mushaf });

  let payload = null;
  let lastStatus = null;

  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        lastStatus = response.status;
        continue;
      }

      payload = await response.json();
      break;
    } catch {
      // Try next candidate endpoint.
    }
  }

  if (!payload) {
    const statusText = lastStatus ? ` (${lastStatus})` : "";
    throw new Error(`Failed to load Quran page ${safePage}${statusText}.`);
  }

  const verses = Array.isArray(payload?.verses) ? payload.verses : [];
  const chapterIds = Array.from(new Set(verses.map((verse) => getChapterId(verse?.verse_key)).filter((id) => Number.isFinite(id))));

  const chapterNames = await loadChapterNames();
  const surahNames = chapterIds.map((id) => chapterNames.get(id) || `سورة ${id}`);

  const juzNumbers = Array.from(
    new Set(verses.map((verse) => Number(verse?.juz_number)).filter((num) => Number.isFinite(num)))
  ).sort((a, b) => a - b);

  return {
    pageNumber: safePage,
    verses,
    ayahs: verses,
    words: flattenWords(verses),
    totalVerses: verses.length,
    totalAyahs: verses.length,
    surahNames,
    juzNumbers,
  };
};

export const getVersePageNumber = async ({ verseKey, mushaf = QURAN_DEFAULTS.mushaf } = {}) => {
  const key = String(verseKey || "").trim();
  if (!key) {
    throw new Error("Invalid verse key.");
  }

  const proxyBase = toVersesBase(QURAN_PROXY_BASE);
  const query = new URLSearchParams({
    words: "false",
    mushaf: String(mushaf),
    fields: "page_number",
  }).toString();

  const encodedKey = encodeURIComponent(key);
  const candidates = [];
  if (proxyBase) {
    candidates.push(`${proxyBase}/verses/by_key/${encodedKey}?${query}`);
  }
  candidates.push(`${QURAN_PUBLIC_API_BASE}/verses/by_key/${encodedKey}?${query}`);

  let payload = null;
  for (const url of candidates) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        continue;
      }
      payload = await response.json();
      break;
    } catch {
      // Try next candidate.
    }
  }

  if (!payload) {
    throw new Error(`Failed to resolve verse page number for ${key}.`);
  }

  const pageNumber =
    Number(payload?.verse?.page_number) ||
    Number(payload?.verse?.pageNumber) ||
    Number(payload?.verses?.[0]?.page_number) ||
    Number(payload?.verses?.[0]?.pageNumber) ||
    null;

  if (!Number.isFinite(pageNumber) || pageNumber < 1 || pageNumber > 604) {
    throw new Error(`Invalid page number for verse ${key}.`);
  }

  return Math.round(pageNumber);
};

export const getVerseByKey = async ({ verseKey, mushaf = QURAN_DEFAULTS.mushaf } = {}) => {
  const key = String(verseKey || "").trim();
  if (!key) {
    throw new Error("Invalid verse key.");
  }

  const proxyBase = toVersesBase(QURAN_PROXY_BASE);
  const query = new URLSearchParams({
    words: "true",
    mushaf: String(mushaf),
    word_fields: "code_v2,text_qpc_hafs,char_type_name,line_number,page_number,position",
  }).toString();

  const encodedKey = encodeURIComponent(key);
  const candidates = [];
  if (proxyBase) {
    candidates.push(`${proxyBase}/verses/by_key/${encodedKey}?${query}`);
  }
  candidates.push(`${QURAN_PUBLIC_API_BASE}/verses/by_key/${encodedKey}?${query}`);

  let payload = null;
  for (const url of candidates) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        continue;
      }

      payload = await response.json();
      break;
    } catch {
      // Try next candidate.
    }
  }

  if (!payload) {
    throw new Error(`Failed to load verse ${key}.`);
  }

  const verse = payload?.verse || payload?.verses?.[0] || null;
  if (!verse) {
    throw new Error(`Verse ${key} not found.`);
  }

  return {
    verse,
    words: flattenWords([verse]),
  };
};

export const searchQuran = async ({
  query,
  language = "en",
  size = 28,
  page = 1,
  mushaf = QURAN_DEFAULTS.mushaf,
} = {}) => {
  const term = String(query || "").trim();
  if (!term) {
    return {
      query: "",
      totalResults: 0,
      currentPage: 1,
      totalPages: 0,
      results: [],
    };
  }

  const safeSize = Math.max(1, Math.min(50, Math.round(Number(size) || 28)));
  const safePage = Math.max(1, Math.round(Number(page) || 1));
  const safeLanguage = toLanguageCode(language);
  const languageCandidates = [safeLanguage, "en", ""]
    .filter((item, index, array) => array.indexOf(item) === index);

  const proxyBase = toVersesBase(QURAN_PROXY_BASE);
  const urls = languageCandidates.flatMap((candidateLanguage) => {
    const params = new URLSearchParams({
      q: term,
      size: String(safeSize),
      page: String(safePage),
      mushaf: String(mushaf),
    });

    if (candidateLanguage) {
      params.set("language", candidateLanguage);
    }

    const paramText = params.toString();
    const candidates = [];
    if (proxyBase) {
      candidates.push(`${proxyBase}/search?${paramText}`);
    }
    candidates.push(`${QURAN_PUBLIC_API_BASE}/search?${paramText}`);
    return candidates;
  });

  let payload = null;
  let lastStatus = null;

  for (const url of urls) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        lastStatus = response.status;
        continue;
      }

      const text = await response.text();
      if (!String(text || "").trim()) {
        continue;
      }

      payload = JSON.parse(text);
      break;
    } catch {
      // Try next candidate endpoint.
    }
  }

  if (!payload) {
    const statusText = lastStatus ? ` (${lastStatus})` : "";
    throw new Error(`Failed to search Quran${statusText}.`);
  }

  const searchRoot = payload?.search && typeof payload.search === "object" ? payload.search : payload;
  const rawResults = Array.isArray(searchRoot?.results)
    ? searchRoot.results
    : Array.isArray(payload?.results)
      ? payload.results
      : [];

  await loadChapterNames();

  const results = rawResults
    .map((item) => {
      const verseKey = String(item?.verse_key || item?.verseKey || "").trim();
      if (!verseKey) {
        return null;
      }

      const chapterPart = Number(String(verseKey).split(":")[0]);
      const ayahPart = Number(String(verseKey).split(":")[1]);
      const chapterName =
        String(item?.verse?.chapter?.name_arabic || "").trim() ||
        String(item?.verse?.chapter?.name_simple || "").trim() ||
        chapterNameCache.get(chapterPart) ||
        `سورة ${chapterPart}`;

      const pageNumber =
        Number(item?.page_number) ||
        Number(item?.verse?.page_number) ||
        Number(item?.verse?.pageNumber) ||
        null;

      return {
        verseId: Number(item?.verse_id) || null,
        verseKey,
        chapterId: Number.isFinite(chapterPart) ? chapterPart : null,
        verseNumber: Number.isFinite(ayahPart) ? ayahPart : null,
        chapterName,
        pageNumber: Number.isFinite(pageNumber) ? Math.round(pageNumber) : null,
        text: cleanRichText(item?.text || item?.highlighted || ""),
        highlightedText: String(item?.highlighted || "").trim(),
      };
    })
    .filter((item) => item && item.verseKey && item.text);

  const totalResults = Number(searchRoot?.total_results || searchRoot?.totalResults) || results.length;
  const totalPages = Number(searchRoot?.total_pages || searchRoot?.totalPages) || Math.ceil(totalResults / safeSize) || 1;
  const currentPage = Number(searchRoot?.current_page || searchRoot?.page) || safePage;

  return {
    query: term,
    totalResults,
    currentPage,
    totalPages,
    results,
  };
};

const cleanRichText = (text) =>
  String(text || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const fetchUrlPayload = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      payload: null,
    };
  }

  const payload = await response.json();
  return {
    ok: true,
    status: response.status,
    payload,
  };
};

const toAbsoluteAudioUrl = (url) => {
  const raw = String(url || "").trim();
  if (!raw) {
    return "";
  }

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  return `${AUDIO_BASE_URL}/${raw.replace(/^\/+/, "")}`;
};

const toLanguageCode = (language) => {
  const code = String(language || "fr").trim().toLowerCase();
  return code || "fr";
};

const normalizeLabel = (value) => String(value || "").trim().toLowerCase();

const toPositiveIntegerOrNull = (value) => {
  const num = Number(value);
  return Number.isInteger(num) && num > 0 ? num : null;
};

export const getTranslationLanguages = async () => {
  if (translationLanguagesCache.length > 0) {
    return translationLanguagesCache;
  }

  if (!translationLanguagesFetchPromise) {
    translationLanguagesFetchPromise = fetch(LANGUAGES_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load translation languages.");
        }

        return response.json();
      })
      .then((payload) => {
        const languages = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.languages)
            ? payload.languages
            : [];
        translationLanguagesCache.length = 0;
        const seenCodes = new Set();
        languages.forEach((item) => {
          const code = String(item?.iso_code || "").trim().toLowerCase();
          if (!code || seenCodes.has(code)) {
            return;
          }

          seenCodes.add(code);

          translationLanguagesCache.push({
            id: Number(item?.id) || null,
            code,
            name: String(item?.name || code),
            nativeName: String(item?.native_name || ""),
            direction: String(item?.direction || "ltr").toLowerCase() === "rtl" ? "rtl" : "ltr",
            translatedName: String(item?.translated_name?.name || ""),
          });
        });

        if (translationLanguagesCache.length === 0) {
          translationLanguagesCache.push(...FALLBACK_TRANSLATION_LANGUAGES);
        }

        return translationLanguagesCache;
      })
      .catch(() => {
        if (translationLanguagesCache.length === 0) {
          translationLanguagesCache.push(...FALLBACK_TRANSLATION_LANGUAGES);
        }
        return translationLanguagesCache;
      });
  }

  return translationLanguagesFetchPromise;
};

export const getTranslationResources = async (language = "fr") => {
  const langCode = toLanguageCode(language);
  if (translationResourceCacheByLanguage.has(langCode)) {
    return translationResourceCacheByLanguage.get(langCode);
  }

  if (!translationResourcesFetchByLanguage.has(langCode)) {
    const promise = getTranslationLanguages()
      .then((languages) => {
        const selectedLanguage = languages.find((item) => item.code === langCode);
        const acceptedLanguageNames = new Set(
          [
            selectedLanguage?.name,
            selectedLanguage?.nativeName,
            selectedLanguage?.translatedName,
          ]
            .map(normalizeLabel)
            .filter(Boolean)
        );

        // Keep this endpoint language stable; this query localizes labels, it does not filter by content language.
        return fetch(`${QURAN_PUBLIC_API_BASE}/resources/translations?language=en`).then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load translation resources for ${langCode}.`);
          }

          return response.json().then((payload) => ({ payload, acceptedLanguageNames }));
        });
      })
      .then((response) => {
        const payload = response?.payload;
        const acceptedLanguageNames = response?.acceptedLanguageNames || new Set();

        const resources = Array.isArray(payload?.translations)
          ? payload.translations
              .map((resource) => ({
                id: Number(resource?.id),
                book: String(resource?.name || "").trim(),
                author: String(resource?.author_name || "").trim(),
                languageName: String(resource?.language_name || "").trim(),
                slug: String(resource?.slug || "").trim(),
              }))
              .filter((item) => {
                if (!Number.isFinite(item.id)) {
                  return false;
                }

                const languageName = normalizeLabel(item.languageName);
                return acceptedLanguageNames.has(languageName);
              })
          : [];

        translationResourceCacheByLanguage.set(langCode, resources);
        return resources;
      })
      .catch(() => {
        translationResourceCacheByLanguage.set(langCode, []);
        return [];
      });

    translationResourcesFetchByLanguage.set(langCode, promise);
  }

  return translationResourcesFetchByLanguage.get(langCode);
};

export const getTafsirResources = async (language = "ar") => {
  const langCode = toLanguageCode(language);
  if (tafsirResourceCacheByLanguage.has(langCode)) {
    return tafsirResourceCacheByLanguage.get(langCode);
  }

  if (!tafsirResourcesFetchByLanguage.has(langCode)) {
    const promise = getTranslationLanguages()
      .then((languages) => {
        const selectedLanguage = languages.find((item) => item.code === langCode);
        const acceptedLanguageNames = new Set(
          [
            selectedLanguage?.name,
            selectedLanguage?.nativeName,
            selectedLanguage?.translatedName,
          ]
            .map(normalizeLabel)
            .filter(Boolean)
        );

        return fetch(`${QURAN_PUBLIC_API_BASE}/resources/tafsirs?language=en`).then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load tafsir resources for ${langCode}.`);
          }

          return response.json().then((payload) => ({ payload, acceptedLanguageNames }));
        });
      })
      .then((response) => {
        const payload = response?.payload;
        const acceptedLanguageNames = response?.acceptedLanguageNames || new Set();

        const resources = Array.isArray(payload?.tafsirs)
          ? payload.tafsirs
              .map((resource) => ({
                id: Number(resource?.id),
                name: String(resource?.name || "").trim(),
                languageName: String(resource?.language_name || "").trim(),
                slug: String(resource?.slug || "").trim(),
              }))
              .filter((item) => {
                if (!Number.isFinite(item.id)) {
                  return false;
                }

                const languageName = normalizeLabel(item.languageName);
                return acceptedLanguageNames.has(languageName);
              })
          : [];

        tafsirResourceCacheByLanguage.set(langCode, resources);
        return resources;
      })
      .catch(() => {
        tafsirResourceCacheByLanguage.set(langCode, []);
        return [];
      });

    tafsirResourcesFetchByLanguage.set(langCode, promise);
  }

  return tafsirResourcesFetchByLanguage.get(langCode);
};

export const getRecitationResources = async (language = "fr") => {
  const langCode = toLanguageCode(language);
  if (recitationResourceCacheByLanguage.has(langCode)) {
    return recitationResourceCacheByLanguage.get(langCode);
  }

  if (!recitationResourcesFetchByLanguage.has(langCode)) {
    const promise = fetch(`${QURAN_PUBLIC_API_BASE}/resources/recitations?language=${encodeURIComponent(langCode)}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load recitation resources for ${langCode}.`);
        }

        return response.json();
      })
      .then((payload) => {
        const resources = Array.isArray(payload?.recitations)
          ? payload.recitations
              .map((resource) => ({
                id: Number(resource?.id),
                reciterName: String(resource?.reciter_name || "").trim(),
                displayName: String(resource?.translated_name?.name || resource?.reciter_name || "").trim(),
                style: String(resource?.style || "").trim(),
                languageName: String(resource?.translated_name?.language_name || "").trim(),
              }))
              .filter((item) => Number.isFinite(item.id) && item.displayName)
          : [];

        recitationResourceCacheByLanguage.set(langCode, resources);
        return resources;
      })
      .catch(async () => {
        if (langCode !== "en") {
          const fallbackResources = await getRecitationResources("en");
          recitationResourceCacheByLanguage.set(langCode, fallbackResources);
          return fallbackResources;
        }

        recitationResourceCacheByLanguage.set(langCode, []);
        return [];
      });

    recitationResourcesFetchByLanguage.set(langCode, promise);
  }

  return recitationResourcesFetchByLanguage.get(langCode);
};

export const getTafsirAvailableLanguages = async () => {
  if (tafsirLanguagesCache.length > 0) {
    return tafsirLanguagesCache;
  }

  if (!tafsirLanguagesFetchPromise) {
    tafsirLanguagesFetchPromise = Promise.all([
      getTranslationLanguages(),
      fetch(`${QURAN_PUBLIC_API_BASE}/resources/tafsirs?language=en`).then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load tafsir language availability.");
        }

        return response.json();
      }),
    ])
      .then(([languages, payload]) => {
        const tafsirs = Array.isArray(payload?.tafsirs) ? payload.tafsirs : [];
        const availableLanguageNames = new Set(
          tafsirs.map((item) => normalizeLabel(item?.language_name)).filter(Boolean)
        );

        tafsirLanguagesCache.length = 0;
        languages.forEach((lang) => {
          const names = [lang?.name, lang?.nativeName, lang?.translatedName]
            .map(normalizeLabel)
            .filter(Boolean);

          if (names.some((name) => availableLanguageNames.has(name))) {
            tafsirLanguagesCache.push(lang);
          }
        });

        return tafsirLanguagesCache;
      })
      .catch(() => tafsirLanguagesCache);
  }

  return tafsirLanguagesFetchPromise;
};

export const getVerseTranslation = async ({ verseKey, language = "fr", translationId } = {}) => {
  const key = String(verseKey || "").trim();
  if (!key) {
    throw new Error("Invalid verse key.");
  }

  const langCode = toLanguageCode(language);
  let lastStatus = null;
  const resourceList = await getTranslationResources(langCode);
  const defaultIds = TRANSLATION_DEFAULT_IDS[langCode] || [];
  const validIds = new Set(resourceList.map((item) => item.id));
  const requestedId = toPositiveIntegerOrNull(translationId);

  let candidateIds = [];

  if (resourceList.length > 0) {
    const selectedFromLanguage = requestedId !== null && validIds.has(requestedId) ? requestedId : null;
    candidateIds = [selectedFromLanguage, ...resourceList.slice(0, 20).map((item) => item.id)].filter(
      (id, index, array) => Number.isFinite(id) && array.indexOf(id) === index
    );
  } else {
    candidateIds = [requestedId, ...defaultIds].filter(
      (id, index, array) => Number.isFinite(id) && array.indexOf(id) === index
    );
  }

  if (candidateIds.length === 0) {
    throw new Error(`No translation resource available for language ${langCode}.`);
  }

  for (const resourceId of candidateIds) {
    try {
      const result = await fetchUrlPayload(
        `${QURAN_PUBLIC_API_BASE}/translations/${resourceId}/by_ayah/${encodeURIComponent(key)}`
      );
      if (!result.ok) {
        lastStatus = result.status;
        continue;
      }

      const payload = result.payload;
      const translations = Array.isArray(payload?.translations) ? payload.translations : [];
      const text = cleanRichText(translations[0]?.text);

      if (text) {
        const resourceMeta = resourceList.find((item) => item.id === resourceId) || {
          book: "",
          author: "",
        };

        return {
          verseKey: key,
          language: langCode,
          translationId: resourceId,
          text,
          book: resourceMeta.book,
          author: resourceMeta.author,
        };
      }
    } catch {
      // Try next translation id.
    }
  }

  const statusText = lastStatus ? ` (${lastStatus})` : "";
  throw new Error(`Failed to load translation for ${key}${statusText}.`);
};

export const getVerseFrenchTranslation = async (verseKey) =>
  getVerseTranslation({ verseKey, language: "fr" });

export const getVerseRecitation = async (input) => {
  const isObjectInput = input && typeof input === "object";
  const key = String(isObjectInput ? input.verseKey : input || "").trim();
  const langCode = toLanguageCode(isObjectInput ? input.language : "fr");
  const requestedId = toPositiveIntegerOrNull(isObjectInput ? input.recitationId : null);
  if (!key) {
    throw new Error("Invalid verse key.");
  }

  let lastStatus = null;

  const resourceList = await getRecitationResources(langCode);
  const availableIds = resourceList.map((item) => item.id);
  const selectedFromLanguage = requestedId !== null && availableIds.includes(requestedId) ? requestedId : null;
  const candidateIds = [selectedFromLanguage, ...availableIds, ...RECITATION_IDS].filter(
    (id, index, array) => Number.isFinite(id) && array.indexOf(id) === index
  );

  if (candidateIds.length === 0) {
    throw new Error(`No recitation resource available for language ${langCode}.`);
  }

  for (const recitationId of candidateIds) {
    try {
      const result = await fetchUrlPayload(
        `${QURAN_PUBLIC_API_BASE}/recitations/${recitationId}/by_ayah/${encodeURIComponent(key)}`
      );
      if (!result.ok) {
        lastStatus = result.status;
        continue;
      }

      const files = Array.isArray(result.payload?.audio_files) ? result.payload.audio_files : [];
      const audioUrl = toAbsoluteAudioUrl(files[0]?.url || "");

      if (audioUrl) {
        const resourceMeta = resourceList.find((item) => item.id === recitationId) || null;

        return {
          verseKey: key,
          language: langCode,
          recitationId,
          audioUrl,
          reciterName: resourceMeta?.displayName || resourceMeta?.reciterName || "",
          style: resourceMeta?.style || "",
        };
      }
    } catch {
      // Try next recitation id.
    }
  }

  const statusText = lastStatus ? ` (${lastStatus})` : "";
  throw new Error(`Failed to load recitation for ${key}${statusText}.`);
};

export const getVerseTafseer = async (input) => {
  const isObjectInput = input && typeof input === "object";
  const key = String(isObjectInput ? input.verseKey : input || "").trim();
  const langCode = toLanguageCode(isObjectInput ? input.language : "ar");
  const requestedId = toPositiveIntegerOrNull(isObjectInput ? input.tafseerId : null);
  if (!key) {
    throw new Error("Invalid verse key.");
  }

  let lastStatus = null;
  const resourceList = await getTafsirResources(langCode);
  const defaultIds = TAFSEER_DEFAULT_IDS[langCode] || [];
  const validIds = new Set(resourceList.map((item) => item.id));

  let candidateIds = [];

  if (resourceList.length > 0) {
    const selectedFromLanguage = requestedId !== null && validIds.has(requestedId) ? requestedId : null;
    candidateIds = [selectedFromLanguage, ...resourceList.slice(0, 20).map((item) => item.id)].filter(
      (id, index, array) => Number.isFinite(id) && array.indexOf(id) === index
    );
  } else {
    candidateIds = [requestedId, ...defaultIds].filter(
      (id, index, array) => Number.isFinite(id) && array.indexOf(id) === index
    );
  }

  if (candidateIds.length === 0) {
    throw new Error(`No tafsir resource available for language ${langCode}.`);
  }

  for (const tafseerId of candidateIds) {
    try {
      const result = await fetchUrlPayload(
        `${QURAN_PUBLIC_API_BASE}/tafsirs/${tafseerId}/by_ayah/${encodeURIComponent(key)}`
      );
      if (!result.ok) {
        lastStatus = result.status;
        continue;
      }

      const text = cleanRichText(result.payload?.tafsir?.text);

      if (text) {
        const resourceMeta = resourceList.find((item) => item.id === tafseerId) || { name: "" };

        return {
          verseKey: key,
          language: langCode,
          tafseerId,
          resourceName: resourceMeta.name || String(result.payload?.tafsir?.resource_name || "").trim(),
          text,
        };
      }
    } catch {
      // Try next tafseer id.
    }
  }

  const statusText = lastStatus ? ` (${lastStatus})` : "";
  throw new Error(`Failed to load tafseer for ${key}${statusText}.`);
};
